'use client'

import { useEffect, useRef } from 'react'

import { UiState, useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  getCatalogItems,
  getCatalogFacetedCategories,
  CATALOG_PER_PAGE,
  ItemsState,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  ItemsReducerAction,
  FacetedCategoriesReducerAction,
  GeneralReducerAction,
} from '@marketplace-web/catalog/catalog-page-data'
import { isResponseSuccessful } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser-data'

import { incrementCatalogLoad } from '../utils/observability'
import useItemsCatalogQuery from './useItemsCatalogQuery'

const useFetchCatalogData = (trackItems: (state: ItemsState) => void) => {
  const { state, dispatch } = useCatalogContext()
  const { page, time, setQuery } = useItemsCatalogQuery()
  const { searchParams: currentSearchParams } = useLocation()

  const filtersRef = useRef(state.filters)
  filtersRef.current = state.filters

  const selectedDynamicFiltersRef = useRef(state.dynamicFilters.selectedDynamicFilters)
  selectedDynamicFiltersRef.current = state.dynamicFilters.selectedDynamicFilters

  const timeRef = useRef(time)
  timeRef.current = time

  const searchQuery = currentSearchParams.search_text ? String(currentSearchParams.search_text) : ''
  const searchQueryRef = useRef(searchQuery)
  searchQueryRef.current = searchQuery

  const searchByImageUuid = currentSearchParams.search_by_image_uuid
    ? String(currentSearchParams.search_by_image_uuid)
    : undefined

  const isInitialLoad = useRef(true)

  const fetchItemsAndFacetedCategories = useLatestCallback(async params => {
    const {
      page: itemPage,
      perPage,
      query,
      time: itemTime,
      searchByImageUuid: itemSearchByImageUuid,
      selectedDynamicFilters,
      ...filters
    } = params

    dispatch({ type: ItemsReducerAction.FETCH_ITEMS_REQUEST, payload: undefined })
    dispatch({
      type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_REQUEST,
      payload: undefined,
    })

    const [itemsResponse, facetedCategoriesResponse] = await Promise.all([
      getCatalogItems(params),
      getCatalogFacetedCategories({
        ...filters,
        selectedDynamicFilters,
        time: itemTime,
        filterSearchText: query,
      }),
    ])

    if (isResponseSuccessful(itemsResponse)) {
      try {
        setQuery('time', itemsResponse.pagination.time)
        dispatch({
          type: GeneralReducerAction.SET_CATALOG_TITLE,
          payload: { title: itemsResponse.title },
        })
        incrementCatalogLoad('success', 'api_items')
      } catch {
        dispatch({
          type: ItemsReducerAction.FETCH_ITEMS_FAILURE,
          payload: { error: itemsResponse.message },
        })
        incrementCatalogLoad('error', 'api_items')
      }

      const itemsPayload = {
        items: itemsResponse.items,
        pagination: itemsResponse.pagination,
        replicaWarning: itemsResponse.replica_warning,
        searchTrackingParams: {
          correlationId: itemsResponse.search_tracking_params.search_correlation_id,
          sessionId: itemsResponse.search_tracking_params.search_session_id,
          globalSearchSessionId:
            itemsResponse.search_tracking_params.global_search_session_id || null,
          globalCatalogBrowseSessionId:
            itemsResponse.search_tracking_params.global_catalog_browse_session_id || null,
        },
      }
      trackItems({ ...itemsPayload, uiState: UiState.Success, error: undefined })

      dispatch({
        type: ItemsReducerAction.FETCH_ITEMS_SUCCESS,
        payload: itemsPayload,
      })
    } else {
      dispatch({
        type: ItemsReducerAction.FETCH_ITEMS_FAILURE,
        payload: { error: itemsResponse.message },
      })

      incrementCatalogLoad('error', 'api_items')
    }

    if (isResponseSuccessful(facetedCategoriesResponse)) {
      dispatch({
        type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_SUCCESS,
        payload: { categories: facetedCategoriesResponse.categories },
      })
    } else {
      dispatch({
        type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_FAILURE,
        payload: { error: facetedCategoriesResponse.message },
      })
    }
  })

  // reset page to 1 when filters change
  useEffect(() => {
    if (state.general.filtersChangeCount <= 1) return

    setQuery('page', 1)
  }, [state.general.filtersChangeCount, setQuery])

  const globalSearchSessionId = useRef(state.items.searchTrackingParams?.globalSearchSessionId)
  globalSearchSessionId.current = state.items.searchTrackingParams?.globalSearchSessionId

  // fetch items when filters, page, or search params change
  useEffect(() => {
    if (state.general.filtersChangeCount === 0) return

    if (isInitialLoad.current) {
      isInitialLoad.current = false

      return
    }

    fetchItemsAndFacetedCategories({
      ...filtersRef.current,
      globalSearchSessionId: globalSearchSessionId.current,
      page,
      query: searchQueryRef.current,
      perPage: CATALOG_PER_PAGE,
      selectedDynamicFilters: selectedDynamicFiltersRef.current,
      time: timeRef.current,
      searchByImageUuid,
    })
  }, [state.general.filtersChangeCount, page, searchByImageUuid, fetchItemsAndFacetedCategories])

  return fetchItemsAndFacetedCategories
}

export default useFetchCatalogData
